import { type FC, useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { chatConversationPath } from "./leapChatPaths";
import { useBlocNext } from "@blac/react";
import translate from "src/lib/translate";
import ChatComposer from "./ChatComposer";
import ChatDetailHeader from "./NavButton";
import {
  ComposerCardArea,
  EmptyChatHero,
  HeroHeading,
  HeroLogo,
  LeapChatContainer,
  LeapChatViewport
} from "./leapChat.styles";
import chatLogo from "src/ui/assets/logos/9am_chat_logo.svg";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import type { LeapChatFileRef } from "./state/types";
import { ConversationService } from "./state/ConversationService";

const NewConversationView: FC = () => {
  const [{ "auth.user.firstname": firstName, "auth.user.nickname": nickName }] =
    useBlocNext(UserPreferencesCubit);
  const navigate = useNavigate();
  const location = useLocation();
  const autoFocus = !!(location.state as { autoFocus?: boolean })?.autoFocus;
  const serviceRef = useRef(ConversationService.getInstance());

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (text: string, fileIds?: string[], files?: LeapChatFileRef[]) => {
      if (isSubmitting) {
        return;
      }

      const service = serviceRef.current;
      setIsSubmitting(true);
      const conversationId = await service.createConversation();
      if (conversationId) {
        service.setPendingFirstMessage(text, fileIds, files);
        navigate(chatConversationPath(conversationId), { replace: true });
      }
      setIsSubmitting(false);
    },
    [isSubmitting, navigate]
  );

  return (
    <LeapChatContainer>
      <LeapChatViewport>
        <ChatDetailHeader showArchive />
        <EmptyChatHero>
          <HeroLogo src={chatLogo} alt="9am" />
          <HeroHeading>
            {translate("leapChat.greeting", {
              name: nickName ?? firstName
            })}
          </HeroHeading>
        </EmptyChatHero>
        <ComposerCardArea
          data-brioche-bottom
          className={"brioche-anchor-override"}
        >
          <ChatComposer
            onSubmit={(text, fileIds, files) =>
              void handleSubmit(text, fileIds, files)
            }
            isRunning={isSubmitting}
            placeholder={translate("leapChat.placeholder.askAnything")}
            autoFocus={autoFocus}
          />
        </ComposerCardArea>
      </LeapChatViewport>
    </LeapChatContainer>
  );
};

export default NewConversationView;
